import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { sendGetRequest, sendPatchRequest, sendPostRequest } from '../../../api/requestUtil';

export const loginUser = createAsyncThunk(
  'login-user',
  async (
    { email, password, preserveNewUserFlag = false }: LoginRequestType,
    thunkAPI
  ) => {
    try {
      const response = await sendPostRequest('api/auth/token/login/', {
        email,
        password,
      });

      // Return both the response AND the flag
      return {
        ...response,
        preserveNewUserFlag, // Pass the flag through
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.detail ||
        error?.non_field_errors?.[0] ||
        'Login failed'
      );
    }
  }
);

export const signUpUser = createAsyncThunk(
  'sign-up-user',
  async (body: SignUpRequestType, { rejectWithValue }) => {
    const url = '/api/auth/users/';
    const token = undefined;
    let headers: any = {};
    if (token) headers['Authorization'] = `Token ${token}`;

    try {
      let response = await axios
        .post(url, body, {
          headers: headers,
        })
        .then(res => res.data)
        .catch(error => Promise.reject(error));
      return response;
    } catch (error: any) {
      let prependMessage = 'Error: ';
      const data = error.response?.data;
      if (data && data instanceof Object) {
        const items = Object.values(data).flat();
        if (items instanceof Array) {
          return rejectWithValue(prependMessage + items[0]);
        }
      }

      rejectWithValue(`An error occured (${error.response.status})`);
    }
  }
);

export const getAllUserInfoByEmail = createAsyncThunk(
  'get-all-user-info-by-email',
  async (email: string, { getState }) => {
    // Logged out, this only says whether the account exists; the token
    // (once logged in) lets the backend return the user's own details.
    const token = (getState() as AppState).user.authToken;
    return sendGetRequest(`/api/get-all-user-info-by-email/${encodeURIComponent(email)}/`, token);
  }
);

export const getUserByUsername = createAsyncThunk(
  'get-user-by-username',
  async (username: string) => {
    return sendGetRequest(`/api/validate-username/${username}`);
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'fetch-current-user',
  async (token: string) => {
    return sendGetRequest('/api/auth/users/me/', token);
  }
);

export const updateEmailOptIn = createAsyncThunk(
  'update-email-opt-in',
  async (emailOptIn: boolean, { getState, rejectWithValue }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    try {
      return await sendPatchRequest('/api/auth/users/me/', { email_opt_in: emailOptIn }, token);
    } catch (error: any) {
      // sendPatchRequest rejects with error.response.data directly (not the axios error).
      if (error && error instanceof Object) {
        const items = Object.values(error).flat();
        if (items instanceof Array && items.length) {
          return rejectWithValue(items[0]);
        }
      }
      return rejectWithValue('Could not save your email preference. Please try again.');
    }
  }
);
